import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel,
  MenuItem, Paper, Select, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Typography
} from '@mui/material';
import { Download, History as HistoryIcon, Preview, Refresh, BarChart as BarChartIcon } from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadStoreWiseStockReportCsv,
  downloadStoreWiseStockReportHistoryCsv,
  downloadStoreWiseStockReportHistoryPdf,
  downloadStoreWiseStockReportPdf,
  fetchStoreWiseStockReportHistory,
  fetchStoreWiseStockReportPreviewData,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const STORE_TYPE_OPTIONS = [
  { value: 'BONDED', label: 'Bonded' },
  { value: 'POL', label: 'POL' },
  { value: 'QUARANTINE', label: 'Quarantine' },
  { value: 'SCRAPE', label: 'Scrape' },
  { value: 'TOOLS', label: 'Tools' }
];
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'IN_INVENTORY', label: 'In Inventory' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'QUARANTINED', label: 'Quarantined' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'UNAVAILABLE', label: 'Unavailable' }
];
const QUARANTINE_VIEW_OPTIONS = [
  { value: '', label: 'All (Serviceable / UN S / Calibrated)' },
  { value: 'SERVICEABLE', label: 'Serviceable' },
  { value: 'UNSERVICEABLE', label: 'Unserviceable (UN S)' },
  { value: 'CALIBRATED', label: 'Calibrated (tools)' }
];

const KIND_UI = {
  STORE_WISE: {
    lockedStoreType: '',
    lockedCategory: '',
    quarantineViews: false,
    fileBase: 'Store_Wise_Stock_Report',
    lockInfo: ''
  },
  BONDED: {
    lockedStoreType: 'BONDED',
    lockedCategory: '',
    quarantineViews: false,
    fileBase: 'Bonded_Store_Stock_Report',
    lockInfo: 'This report is fixed to Bonded store stock.'
  },
  POL: {
    lockedStoreType: 'POL',
    lockedCategory: '',
    quarantineViews: false,
    fileBase: 'POL_Store_Stock_Report',
    lockInfo: 'This report is fixed to POL store stock.'
  },
  GSE: {
    lockedStoreType: '',
    lockedCategory: 'GSE',
    quarantineViews: false,
    fileBase: 'GSE_Stock_Report',
    lockInfo: 'This report is fixed to GSE category stock across stores.'
  },
  TOOLS: {
    lockedStoreType: 'TOOLS',
    lockedCategory: '',
    quarantineViews: false,
    fileBase: 'Tool_Store_Stock_Report',
    lockInfo: 'This report is fixed to Tool store stock.'
  },
  QUARANTINE: {
    lockedStoreType: 'QUARANTINE',
    lockedCategory: '',
    quarantineViews: true,
    fileBase: 'Quarantine_Store_Report',
    lockInfo: 'This report is fixed to Quarantine store stock. Filter by Serviceable, Unserviceable, or Calibrated tools.'
  },
  SCRAPE: {
    lockedStoreType: 'SCRAPE',
    lockedCategory: '',
    quarantineViews: false,
    fileBase: 'Scrap_Store_Report',
    lockInfo: 'This report is fixed to Scrap (Scrape) store stock.'
  }
};

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * @param {{
 *   reportKind?: 'STORE_WISE'|'BONDED'|'POL'|'GSE'|'TOOLS'|'QUARANTINE'|'SCRAPE',
 *   lockedStoreType?: string,
 *   title?: string,
 *   description?: string
 * }} props
 */
const StoreWiseStockReportPanel = ({
  reportKind = 'STORE_WISE',
  lockedStoreType = '',
  title = 'Store-wise Stock Report',
  description = 'Stock summarised by store type (Bonded, POL, Quarantine, Scrape, Tools), plus line-level detail.'
}) => {
  const { showToast } = useToast();
  const kind = String(reportKind || 'STORE_WISE').toUpperCase();
  const ui = KIND_UI[kind] || KIND_UI.STORE_WISE;
  const effectiveLockedStore = lockedStoreType || ui.lockedStoreType;
  const isLocked = Boolean(effectiveLockedStore || ui.lockedCategory || kind !== 'STORE_WISE');

  const [subTab, setSubTab] = useState(0);
  const [storeTypes, setStoreTypes] = useState(effectiveLockedStore ? [effectiveLockedStore] : []);
  const [status, setStatus] = useState('');
  const [quarantineView, setQuarantineView] = useState('');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchStoreWiseStockReportHistory({ reportKind: kind }));
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast, kind]);

  useEffect(() => { if (subTab === 1) loadHistory(); }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    reportKind: kind,
    storeTypes: effectiveLockedStore ? [effectiveLockedStore] : storeTypes,
    category: ui.lockedCategory || '',
    quarantineView: ui.quarantineViews ? quarantineView : '',
    status,
    search,
    includeArchived,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [kind, effectiveLockedStore, storeTypes, ui.lockedCategory, ui.quarantineViews, quarantineView, status, search, includeArchived, dateFrom, dateTo, reportDate]);

  const busy = previewing || downloading || downloadingCsv || visualizing;
  const fileBase = ui.fileBase;

  const runPreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      setPreviewReportData(await fetchStoreWiseStockReportPreviewData(reportPayload));
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally { setPreviewing(false); }
  };
  const runVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      setVisualizeData(await fetchStoreWiseStockReportPreviewData(reportPayload));
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally { setVisualizing(false); }
  };
  const runDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadStoreWiseStockReportPdf({ ...reportPayload, visualizations }, `${fileBase}_${fileStamp}.pdf`);
      showToast(`${title} downloaded and saved to history`, 'success');
      if (subTab === 1) loadHistory();
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally { setDownloading(false); }
  };
  const runCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadStoreWiseStockReportCsv(reportPayload, `${fileBase}_${fileStamp}.csv`);
      showToast(`${title} CSV downloaded`, 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally { setDownloadingCsv(false); }
  };

  const historyExtraLabel = (row) => {
    if (kind === 'QUARANTINE' && row.quarantineView) {
      const opt = QUARANTINE_VIEW_OPTIONS.find((o) => o.value === row.quarantineView);
      return opt?.label || row.quarantineView;
    }
    if (kind === 'GSE') return 'GSE';
    return (row.storeTypes || []).length ? row.storeTypes.join(', ') : 'All';
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{title}</Typography>
      <Typography color="text.secondary" paragraph>{description}</Typography>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            {!isLocked && (
              <FormControl fullWidth>
                <InputLabel>Store types</InputLabel>
                <Select
                  multiple
                  value={storeTypes}
                  label="Store types"
                  onChange={(e) => {
                    const value = e.target.value;
                    setStoreTypes(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) => (selected.length ? selected.join(', ') : 'All store types')}
                >
                  {STORE_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Optional — leave empty for all stores
                </Typography>
              </FormControl>
            )}
            {isLocked && ui.lockInfo && (
              <Alert severity="info">{ui.lockInfo}</Alert>
            )}
            {ui.quarantineViews && (
              <FormControl fullWidth>
                <InputLabel>Condition view</InputLabel>
                <Select
                  value={quarantineView}
                  label="Condition view"
                  onChange={(e) => setQuarantineView(e.target.value)}
                >
                  {QUARANTINE_VIEW_OPTIONS.map((o) => (
                    <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth placeholder="Part number, description…" />
            <FormControlLabel control={<Switch checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />} label="Include archived items" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
            </Stack>
            {dateRangeError && <Alert severity="warning">{dateRangeError}</Alert>}
            <TextField label="Report Date" type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />} onClick={runVisualize} disabled={busy || Boolean(dateRangeError)}>Visualize</Button>
              <Button variant="outlined" startIcon={previewing ? <CircularProgress size={16} /> : <Preview />} onClick={runPreview} disabled={busy || Boolean(dateRangeError)}>Preview</Button>
              <Button variant="contained" startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />} onClick={() => runDownload()} disabled={busy || Boolean(dateRangeError)}>Download PDF</Button>
              <Button variant="outlined" startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />} onClick={runCsv} disabled={busy || Boolean(dateRangeError)}>Download CSV</Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded {title}s</Typography>
            <Button size="small" startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />} onClick={loadHistory} disabled={historyLoading}>Refresh</Button>
          </Box>
          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center"><CircularProgress size={28} /></Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No downloads yet. Generate one from the Generate tab.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>{kind === 'QUARANTINE' ? 'View' : 'Stores'}</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{historyExtraLabel(row)}</TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" startIcon={<Download />} onClick={() => downloadStoreWiseStockReportHistoryPdf(row._id, row.filename)}>PDF</Button>
                        <Button size="small" startIcon={<Download />} onClick={() => downloadStoreWiseStockReportHistoryCsv(row._id, row.filename)}>CSV</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog open={previewOpen} title={`${title} preview`} reportData={previewReportData} onClose={() => { setPreviewOpen(false); setPreviewReportData(null); }} onDownload={() => runDownload()} downloading={downloading} onDownloadCsv={runCsv} downloadingCsv={downloadingCsv} />
      <ReportVisualizeDialog open={visualizeOpen} title={title} data={visualizeData} downloading={downloading} onClose={() => { setVisualizeOpen(false); setVisualizeData(null); }} onDownloadPdfWithVisualization={(v) => runDownload(v)} />
    </Box>
  );
};

export default StoreWiseStockReportPanel;
